import { useRef, useState, type DragEvent } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Switch from "@/v2-ui/switch"
import Popover from "@/v2-ui/popover"
import Button, { TooltipButton } from "@/v2-backoffice-client/components/button"
import type { InfoGridState } from "./useInfoGridState"

type InfoGridToolbarProps = {
  className?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: InfoGridState<any>
}

function InfoGridToolbar(props: InfoGridToolbarProps) {
  const { state, className } = props
  const {
    allFields,
    hideEmpty,
    isFieldVisible,
    toggleField,
    showAllFields,
    hideAllFields,
    toggleHideEmpty,
    reorderFields,
    reset,
    hasCustomization,
  } = state

  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dropTarget, setDropTarget] = useState<number | null>(null)
  const dragCounter = useRef(0)

  function handleDragStart(e: DragEvent, index: number) {
    setDragIndex(index)
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", String(index))
  }

  function handleDragOver(e: DragEvent, index: number) {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    if (dragIndex !== null && index !== dragIndex) {
      setDropTarget(index)
    }
  }

  function handleDragEnter(e: DragEvent) {
    e.preventDefault()
    dragCounter.current++
  }

  function handleDragLeave() {
    dragCounter.current--
    if (dragCounter.current === 0) {
      setDropTarget(null)
    }
  }

  function handleDrop(e: DragEvent, toIndex: number) {
    e.preventDefault()
    dragCounter.current = 0
    if (dragIndex !== null && dragIndex !== toIndex) {
      reorderFields(dragIndex, toIndex)
    }
    setDragIndex(null)
    setDropTarget(null)
  }

  function handleDragEnd() {
    dragCounter.current = 0
    setDragIndex(null)
    setDropTarget(null)
  }

  return (
    <div className={cn("info-grid-toolbar", className)}>
      <Popover>
        <Popover.Trigger asChild>
          <TooltipButton
            variant="ghost"
            size="tiny"
            isSquare
            iconSrc="settings"
            content="Customize fields"
          />
        </Popover.Trigger>

        <Popover.Content
          align="end"
          side="bottom"
          className="info-grid-settings"
        >
          <div className="info-grid-settings-header">
            <span className="info-grid-settings-title">Fields</span>

            <div className="info-grid-settings-actions">
              <Button
                variant="ghost"
                size="xs"
                className="info-grid-settings-action"
                onClick={showAllFields}
              >
                Show All
              </Button>

              <Button
                variant="ghost"
                size="xs"
                className="info-grid-settings-action"
                onClick={hideAllFields}
              >
                Hide All
              </Button>

              {hasCustomization && (
                <Button
                  variant="ghost"
                  size="xs"
                  className="info-grid-settings-action"
                  onClick={reset}
                >
                  Reset
                </Button>
              )}
            </div>
          </div>

          <div className="info-grid-settings-list">
            {allFields.map((field, index) => {
              const isDragging = dragIndex === index
              const isDropTarget = dropTarget === index

              return (
                <div
                  key={field.id}
                  className={[
                    "info-grid-settings-item",
                    isDragging && "info-grid-settings-item-dragging",
                    isDropTarget && "info-grid-settings-item-drop-target",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <Icon
                    src="grip-dots-vertical"
                    size={12}
                    className="info-grid-settings-grip"
                  />
                  <span className="info-grid-settings-label">
                    {field.label}
                  </span>
                  <Switch
                    checked={isFieldVisible(field.id)}
                    onCheckedChange={() => toggleField(field.id)}
                  />
                </div>
              )
            })}
          </div>

          <div className="info-grid-settings-option">
            <span className="info-grid-settings-label">Hide empty</span>
            <Switch checked={hideEmpty} onCheckedChange={toggleHideEmpty} />
          </div>
        </Popover.Content>
      </Popover>
    </div>
  )
}

export default InfoGridToolbar
